/**
 * LiveTestStage — onboarding step 6, talking to the REAL genie.
 *
 * The genie exists by this point, and it already has a public page. So the
 * honest test is the page itself, embedded, rather than a second conversation
 * implementation that could drift from what customers actually get. This is
 * literally what a customer sees.
 *
 * `?embed=true` is the app's existing embed contract (App.tsx suppresses app
 * chrome for it), so nothing new is needed on the page side.
 *
 * When there is no genie or no page yet, the caller falls back to the scripted
 * preview in TestStage, which is labelled as a preview rather than passed off
 * as the real thing.
 */
import { useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";

interface LiveTestStageProps {
  /** The genie's public page slug. */
  urlName: string;
  /** Fires the first time the owner engages with the live genie. */
  onTested?: () => void;
}

export const LiveTestStage = ({ urlName, onTested }: LiveTestStageProps) => {
  const [loaded, setLoaded] = useState(false);
  const src = `/live-agent/${urlName}?embed=true`;
  const openUrl = `/live-agent/${urlName}`;

  return (
    <div data-hg-block="live-test-stage" className="w-full flex flex-col items-center gap-4">
      <div
        className="relative w-full max-w-[520px] overflow-hidden rounded-2xl border border-genie-border bg-white shadow-sm"
        style={{ height: 520 }}
        onMouseDown={() => onTested?.()}
        onTouchStart={() => onTested?.()}
      >
        {!loaded && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white">
            <Loader2 className="h-6 w-6 animate-spin text-genie-primary" />
            <p className="text-[13px] text-[#64748B]">Waking your genie up</p>
          </div>
        )}
        <iframe
          title="Your genie"
          src={src}
          className="h-full w-full border-0"
          allow="microphone; autoplay; clipboard-write"
          onLoad={() => {
            setLoaded(true);
            onTested?.();
          }}
        />
      </div>

      <a
        href={openUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-genie-primary hover:opacity-80 transition-opacity"
      >
        <ExternalLink className="w-3.5 h-3.5" />
        Open your genie's page in a new tab
      </a>

      <p className="max-w-[420px] text-center text-[12.5px] text-gray-500">
        This is your genie's real page, exactly as a customer sees it. Anything you say here is a
        real conversation.
      </p>
    </div>
  );
};

export default LiveTestStage;
